import { Alert, Snackbar, alpha, SxProps, Theme, AlertColor } from "@mui/material"
import { createContext, FC, ReactNode, useContext, useState } from "react"
import { Spinner } from "../components"

interface SnackbarContextType {
    showMessage: (
        message: string,
        options?: {
            severity?: AlertColor
            position?: SnackbarState["position"]
            autoHideDuration?: number
            isLoading?: boolean
        }
    ) => void
    closeMessage: () => void
}

const SnackbarContext = createContext<SnackbarContextType>({
    showMessage: () => {},
    closeMessage: () => {},
})

export const useSnackbar = () => useContext(SnackbarContext)

type Vertical = "top" | "bottom"
type Horizontal = "left" | "right" | "center"

interface SnackbarState {
    open: boolean
    message: string | null
    severity: AlertColor | null
    position: `${Vertical}-${Horizontal}`
    autoHideDuration: number
    isLoading: boolean
}

export interface SnackbarOptions {
    severity?: AlertColor
    position?: SnackbarState["position"]
    autoHideDuration?: number
    isLoading?: boolean
}

export const SnackbarProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: null,
        severity: null,
        position: "bottom-center",
        autoHideDuration: 4000,
        isLoading: false,
    })

    const showMessage = (message: string, options: SnackbarOptions = {}) => {
        const {
            severity = "success",
            position = "bottom-center",
            autoHideDuration = 4000,
            isLoading = false,
        } = options
        setSnackbar({ open: true, message, severity, position, autoHideDuration, isLoading })
    }

    const closeMessage = () => {
        setSnackbar((prev) => ({ ...prev, open: false }))
    }

    const [vertical, horizontal] = snackbar.position.split("-") as [Vertical, Horizontal]

    return (
        <SnackbarContext.Provider value={{ showMessage, closeMessage }}>
            {children}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={snackbar.autoHideDuration}
                onClose={closeMessage}
                anchorOrigin={{
                    vertical,
                    horizontal,
                }}
            >
                <Alert
                    onClose={snackbar.isLoading ? undefined : closeMessage}
                    severity={snackbar.severity || "success"}
                    sx={styles.snackbar}
                    icon={snackbar.isLoading ? <Spinner size={20} /> : undefined}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    )
}

const styles: Record<string, SxProps<Theme>> = {
    snackbar: {
        border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
    },
}
